import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { CreditCard, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

export function RampCardAccess() {
  const [data, setData] = useState({ users: [], cardholders: [] });
  const [loading, setLoading] = useState(true);
  const [openUser, setOpenUser] = useState(null);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/ramp/access"); setData(data); }
    catch (e) { setData({ users: [], cardholders: [] }); toast.error("Ramp card access couldn't load", { description: e.userMessage }); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (u) => { setOpenUser(u.email); setDraft(u.cardholders || []); };
  const toggle = (name) => setDraft((d) => d.includes(name) ? d.filter((x) => x !== name) : [...d, name]);
  const save = async (email) => {
    setSaving(true);
    try {
      await api.put("/ramp/access", { email, cardholders: draft });
      toast.success("Card access updated");
      setOpenUser(null); load();
    } catch (err) { toast.error("Could not save", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setSaving(false); }
  };

  const nonAdmin = (data.users || []).filter((u) => u.role === "regional" || u.role === "community");

  return (
    <div className="bg-brand-surface border border-brand-border rounded-md p-6" data-testid="ramp-card-access">
      <div className="flex items-center gap-2 mb-1">
        <CreditCard className="h-4 w-4 text-brand-tertiary" />
        <h3 className="text-brand-ink">Ramp card access</h3>
      </div>
      <p className="text-[12px] text-brand-muted mb-5 max-w-2xl">Pick which cards each user can see on the Card Charges page. A user with no cards linked sees nothing. Admins always see every card.</p>
      {loading ? <p className="text-sm text-brand-tertiary py-4">Loading…</p> : (
        <div className="divide-y divide-brand-border/60">
          {nonAdmin.length === 0 && <p className="text-sm text-brand-tertiary py-4">No non-admin users yet.</p>}
          {nonAdmin.map((u) => (
            <div key={u.email} className="py-3" data-testid={`ramp-access-user-${u.email}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-brand-ink truncate">{u.name || u.email} <span className="text-brand-tertiary text-xs">· {u.role}</span></p>
                  <p className="text-[11px] text-brand-tertiary truncate">
                    {u.cardholders?.length ? `${u.cardholders.length} card${u.cardholders.length === 1 ? "" : "s"}: ${u.cardholders.join(", ")}` : "No cards — sees nothing"}
                  </p>
                </div>
                {openUser === u.email
                  ? <div className="flex gap-2 shrink-0">
                      <button onClick={() => setOpenUser(null)} className="text-xs px-3 py-1.5 text-brand-muted">Cancel</button>
                      <button onClick={() => save(u.email)} disabled={saving} data-testid={`ramp-access-save-${u.email}`} className="text-xs bg-brand-primary text-brand-page px-3 py-1.5 rounded disabled:opacity-40">{saving ? "Saving…" : "Save"}</button>
                    </div>
                  : <button onClick={() => startEdit(u)} data-testid={`ramp-access-edit-${u.email}`} className="text-xs border border-brand-border rounded px-3 py-1.5 text-brand-muted hover:text-brand-ink shrink-0">Edit cards</button>}
              </div>
              {openUser === u.email && (
                <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid={`ramp-access-picker-${u.email}`}>
                  {(data.cardholders || []).map((c) => {
                    const on = draft.includes(c.cardholder_name);
                    return (
                      <button key={c.cardholder_name} onClick={() => toggle(c.cardholder_name)} data-testid={`ramp-access-chip-${c.cardholder_name}`}
                        className={`flex items-center gap-2 text-xs px-3 py-2 rounded border text-left ${on ? "border-brand-primary bg-brand-primary/10 text-brand-ink" : "border-brand-border text-brand-muted hover:text-brand-ink"}`}>
                        <span className={`h-3.5 w-3.5 rounded flex items-center justify-center border ${on ? "bg-brand-primary border-brand-primary" : "border-brand-border"}`}>{on && <Check className="h-2.5 w-2.5 text-brand-page" />}</span>
                        <span className="truncate">{c.cardholder_name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
